// src/pages/cartPage.jsx - FIXED with real-time stock validation
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/cartContext';
import { getProductDetails } from '../store/api/products';
import api from '../store/api/base';
import Loader from '../components/common/loader';

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    cart, 
    itemCount, 
    totalAmount, 
    loading, 
    error, 
    updateItem, 
    removeItem, 
    emptyCart,
    isAuthenticated
  } = useCart();

  const [pincode, setPincode] = useState('');
  const [checkingPincode, setCheckingPincode] = useState(false);
  const [deliveryInfo, setDeliveryInfo] = useState(null);
  const [pincodeError, setPincodeError] = useState('');
  
  // ✅ NEW: Track real-time stock data
  const [stockData, setStockData] = useState({});
  const [loadingStock, setLoadingStock] = useState(false);

  // ✅ NEW: Fetch real-time stock for guest cart items
  useEffect(() => {
    const fetchStockData = async () => {
      // Only fetch stock for guest users (authenticated users get fresh data from backend)
      if (isAuthenticated || !cart.items || cart.items.length === 0) {
        return;
      }

      // console.log('📦 Fetching real-time stock for guest cart items');
      setLoadingStock(true);

      try {
        const stockPromises = cart.items.map(item => 
          getProductDetails(item.productId)
            .then(response => ({
              productId: item.productId,
              stock: response.data.product.stock,
              stockAvailable: response.data.product.stock,
              isInStock: response.data.product.stock > 0,
              stockStatus: response.data.product.stock === 0 ? 'OUT_OF_STOCK' :
                          response.data.product.stock <= 10 ? 'LOW_STOCK' : 'IN_STOCK'
            }))
            .catch(error => {
              // console.error(`Failed to fetch stock for ${item.productId}:`, error);
              return {
                productId: item.productId,
                stock: 0,
                stockAvailable: 0,
                isInStock: false,
                stockStatus: 'OUT_OF_STOCK'
              };
            })
        );

        const stockResults = await Promise.all(stockPromises);
        
        // Create stock lookup map
        const stockMap = {};
        stockResults.forEach(result => {
          stockMap[result.productId] = result;
        });

        setStockData(stockMap);
        // console.log('✅ Stock data fetched:', stockMap);
      } catch (error) {
        // console.error('❌ Error fetching stock data:', error);
      } finally {
        setLoadingStock(false);
      }
    };

    fetchStockData();
  }, [cart.items, isAuthenticated]);

  // ✅ NEW: Get current stock for an item
  const getCurrentStock = (item) => {
    if (isAuthenticated) {
      // Authenticated users: use data from backend (already fresh)
      return {
        stock: item.stock || 0,
        stockAvailable: item.stockAvailable || item.stock || 0,
        isInStock: item.isInStock !== undefined ? item.isInStock : (item.stock > 0),
        stockStatus: item.stockStatus || 'IN_STOCK'
      };
    } else {
      // Guest users: use fetched stock data
      return stockData[item.productId] || {
        stock: item.stock || 0,
        stockAvailable: item.stockAvailable || item.stock || 0,
        isInStock: item.isInStock !== undefined ? item.isInStock : false,
        stockStatus: item.stockStatus || 'OUT_OF_STOCK'
      };
    }
  };

  const handleCheckPincode = async () => {
    if (!pincode || pincode.length !== 6) {
      setPincodeError('Please enter a valid 6-digit pincode');
      return;
    }

    setCheckingPincode(true);
    setPincodeError('');
    setDeliveryInfo(null);

    try {
      const response = await api.get(`/shipping/pincode/${pincode}`);
      const data = response.data;

      if (data.status === 'success' && data.data) {
        const info = data.data;
        if (info.isServiceable) {
          const estimatedDays = info.expectedDeliveryDays || 4;
          const deliveryDate = new Date(
            Date.now() + estimatedDays * 24 * 60 * 60 * 1000
          ).toLocaleDateString('en-IN', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
          });

          setDeliveryInfo({
            serviceable: true,
            pincode,
            estimatedDays,
            courierName: info.courierName,
            shippingRate: info.shippingRate,
            message: `Delivery in ${estimatedDays} business day${estimatedDays !== 1 ? 's' : ''}`,
            deliveryDate
          });
        } else {
          setDeliveryInfo({ serviceable: false, pincode });
        }
      } else {
        setDeliveryInfo({ serviceable: false, pincode });
      }
    } catch (err) {
      setPincodeError('Failed to check delivery. Please try again.');
    } finally {
      setCheckingPincode(false);
    }
  };

  const calculateDiscount = (mrp, price) => {
    if (!mrp || !price || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  };

  // ✅ UPDATED: Use real-time stock data
  const getStockBadge = (item) => {
    const currentStock = getCurrentStock(item);
    const stock = currentStock.stockAvailable || currentStock.stock || 0;
    
    if (!stock || stock === 0) {
      return <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Out of Stock</span>;
    }
    if (stock <= 10) {
      return <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Low Stock ({stock})</span>;
    }
    return <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">In Stock</span>;
  };

 const handleQuantityChange = (productId, color, quantity, sizeLabel) => {
    const item = cart.items.find(i => i.productId === productId && i.color === color && (i.sizeLabel || null) === (sizeLabel || null));
    if (item) {
      const currentStock = getCurrentStock(item);
      const maxStock = currentStock.stockAvailable || currentStock.stock || 0;
      
      if (quantity > maxStock) {
        alert(`Only ${maxStock} items available in stock`);
        return;
      }
    }
    
updateItem({ productId, color, quantity, sizeLabel: sizeLabel || null });
  };

  const handleRemoveItem = (productId, color, sizeLabel) => {
    removeItem(productId, color, sizeLabel);
  };

  const isCartEmpty = !cart.items || cart.items.length === 0;

  const handleCheckout = () => {
    // ✅ Validate stock before checkout
    if (!isAuthenticated) {
      const outOfStockItems = cart.items.filter(item => {
        const currentStock = getCurrentStock(item);
        return !currentStock.isInStock || currentStock.stockAvailable < item.quantity;
      });

      if (outOfStockItems.length > 0) {
        alert('Some items in your cart are out of stock or have insufficient quantity. Please review your cart.');
        return;
      }
    }
    
    navigate('/checkout');
  };

 const calculateFinalTotal = () => {
    const subtotal = cart.subtotal || 0;
    const gst = cart.gstCalculation?.totalGst || 0;
    return subtotal + gst;
  };

  if (loading || loadingStock) {
    return (
      <div className="container mx-auto px-4 py-8 flex justify-center items-center min-h-[400px]">
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6">Your Shopping Cart</h1>

      {error && (
        <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
          {error}
        </div>
      )}

      {/* ✅ NEW: Show warning for guest users if stock data is being fetched */}
      {!isAuthenticated && !loadingStock && Object.keys(stockData).length > 0 && (
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> Stock availability has been updated. Please review your cart before checkout.
          </p>
        </div>
      )}

      {isCartEmpty ? (
        <div className="bg-gray-50 p-8 text-center rounded-lg">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
          <p className="text-gray-600 mb-4">Your cart is empty.</p>
          <Link 
            to="/products" 
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition inline-block"
          >
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">Cart Items ({itemCount})</h2>
                <button
                  className="text-red-600 hover:text-red-800 font-medium text-sm"
                  onClick={emptyCart}
                >
                  Clear Cart
                </button>
              </div>

              <ul className="divide-y divide-gray-200">
                {cart.items && cart.items.map((item, index) => {
                  const uniqueKey = `${item.productId}-${item.color || 'default'}-${index}-${item.quantity}`;
                  const currentStock = getCurrentStock(item);
                 const maxAvailableStock = currentStock.stockAvailable || currentStock.stock || (item.sizeLabel ? 999 : 0);
                  return (
                    <li key={uniqueKey} className="p-4">
                      {/* ✅ Show out of stock warning */}
                      {!currentStock.isInStock && (
                        <div className="mb-3 bg-red-50 border border-red-200 rounded p-2">
                          <p className="text-sm text-red-800">
                            ⚠️ This item is currently out of stock
                          </p>
                        </div>
                      )}
                      
                      {/* ✅ Show insufficient stock warning */}
                      {currentStock.isInStock && item.quantity > maxAvailableStock && (
                        <div className="mb-3 bg-yellow-50 border border-yellow-200 rounded p-2">
                          <p className="text-sm text-yellow-800">
                            ⚠️ Only {maxAvailableStock} items available. Please adjust quantity.
                          </p>
                        </div>
                      )}
                      
                      <div className="flex flex-col sm:flex-row gap-4">
                        {/* Product Image */}
                        <div className="w-24 h-24 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                          <Link to={`/product/${item.productId}`}>
                            <img
                              src={item.thumbnailUrl || item.imageUrl || '/images/placeholder.png'}
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </Link>
                        </div>

                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/product/${item.productId}`} 
                            className="text-lg font-medium text-gray-800 hover:text-blue-600 block truncate"
                          >
                            {item.name}
                          </Link>

                          <div className="mt-2 space-y-1">
                            {item.brand && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Brand:</span> {item.brand}
                              </p>
                            )}
                            
                            {item.manufacturer && (
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Manufacturer:</span> {item.manufacturer}
                              </p>
                            )}

                            <div className="flex flex-wrap gap-3 text-sm text-gray-600">
                              {item.material && (
                                <span>
                                  <span className="font-medium">Material:</span> {item.material}
                                </span>
                              )}
                              
                              {item.color && (
                                <span>
                                  <span className="font-medium">Color:</span> {item.color}
                                </span>
                              )}
                              
                              {item.thickness?.value && (
                                <span>
                                  <span className="font-medium">Thickness:</span> {item.thickness.value}{item.thickness.unit}
                                </span>
                              )}
                            </div>

                            {/* Stock Status - Using real-time data */}
                            <div className="mt-2">
                              {getStockBadge(item)}
                            </div>
                          </div>

                          {/* Pricing */}
                          <div className="flex items-center mt-3">
                            <span className="text-xl font-bold text-gray-900">
                              ₹{(item.price || 0).toLocaleString('en-IN')}
                            </span>

                            {item.mrp > item.price && (
                              <>
                                <span className="text-sm text-gray-500 line-through ml-2">
                                  ₹{(item.mrp || 0).toLocaleString('en-IN')}
                                </span>

                                <span className="text-sm text-green-600 ml-2 font-medium">
                                  {calculateDiscount(item.mrp, item.price)}% off
                                </span>
                              </>
                            )}
                          </div>

                          {item.isBulkItem && (
                            <div className="mt-2">
                              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                Bulk Discount Applied
                              </span>
                            </div>
                          )}

                          {item.customerNotes && (
                            <p className="text-sm text-gray-500 mt-2 italic">
                              Note: {item.customerNotes}
                            </p>
                          )}
                        </div>

                        {/* Quantity Controls & Actions */}
                        <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-4">
                          <div className="flex items-center border rounded-lg overflow-hidden">
                            <button
                              className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                             onClick={() => handleQuantityChange(item.productId, item.color, item.quantity - 1, item.sizeLabel)}
                              disabled={item.quantity <= (item.minOrderQuantity || 1)}
                            >
                              −
                            </button>
                            <span className="w-12 h-10 flex items-center justify-center text-center font-medium border-x">
                              {item.quantity}
                            </span>
                            <button
                              className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              onClick={() => handleQuantityChange(item.productId, item.color, item.quantity + 1, item.sizeLabel)}
                              disabled={(maxAvailableStock > 0 && item.quantity >= maxAvailableStock) ||(!item.sizeLabel && !currentStock.isInStock)}
                            >
                              +
                            </button>
                          </div>

                          <div className="text-right sm:mt-2">
                            <p className="text-sm text-gray-600">Subtotal</p>
                            <p className="text-lg font-bold text-gray-900">
                              ₹{((item.price || 0) * (item.quantity || 0)).toLocaleString('en-IN')}
                            </p>
                          </div>

                          <button
                            className="text-red-500 hover:text-red-700 p-2"
                            onClick={() => handleRemoveItem(item.productId, item.color, item.sizeLabel)}
                            title="Remove item"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Order Summary - Rest remains the same */}
          <div>
            <div className="bg-white rounded-lg shadow overflow-hidden sticky top-20">
              {/* Pincode Checker */}
              <div className="p-4 border-b bg-blue-50">
                <h3 className="font-semibold mb-3 text-gray-800">Check Delivery</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter pincode"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyPress={(e) => e.key === 'Enter' && handleCheckPincode()}
                    className="flex-1 px-3 py-2 border rounded focus:outline-none focus:border-blue-500"
                    maxLength="6"
                  />
                  <button
                    onClick={handleCheckPincode}
                    disabled={checkingPincode || pincode.length !== 6}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {checkingPincode ? '...' : 'Check'}
                  </button>
                </div>
                
                {pincodeError && (
                  <p className="text-red-600 text-sm mt-2">{pincodeError}</p>
                )}
                
                {deliveryInfo && (
                  <div className="mt-3 p-3 bg-white rounded border border-blue-200">
                    {deliveryInfo.serviceable ? (
                      <div className="flex items-start gap-2 mb-2">
                        <svg className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <div>
                          <p className="font-medium text-green-700">Available for delivery</p>
                          <p className="text-sm text-gray-600 mt-1">{deliveryInfo.message}</p>
                          <p className="text-sm text-gray-600">Expected by: <span className="font-medium">{deliveryInfo.deliveryDate}</span></p>
                          {deliveryInfo.courierName && (
                            <p className="text-xs text-blue-600 mt-1">🚚 via {deliveryInfo.courierName}</p>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                        <div>
                          <p className="font-medium text-red-700">Not serviceable</p>
                          <p className="text-sm text-gray-600">Sorry, we don't deliver to this pincode yet.</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="p-4 border-b">
                <h2 className="text-lg font-semibold">Order Summary</h2>
              </div>

              <div className="p-4 space-y-4">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                  <span className="font-medium">
                    ₹{(cart.subtotal || totalAmount || 0).toLocaleString('en-IN')}
                  </span>
                </div>

                {cart.bulkDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Bulk Discount</span>
                    <span className="font-medium">−₹{(cart.bulkDiscount || 0).toLocaleString('en-IN')}</span>
                  </div>
                )}

                {cart.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span className="font-medium">−₹{(cart.discount || 0).toLocaleString('en-IN')}</span>
                  </div>
                )}

                <div className="flex justify-between text-gray-600">
                  <span>Shipping</span>
                  <span className="font-medium text-gray-400 text-sm italic">
                    Calculated at checkout
                  </span>
                </div>

                {cart.gstCalculation?.totalGst > 0 && (
                  <div className="flex justify-between text-gray-600">
                    <span>GST</span>
                    <span className="font-medium">
                      ₹{(cart.gstCalculation.totalGst || 0).toLocaleString('en-IN')}
                    </span>
                  </div>
                )}

                <div className="border-t pt-4 flex justify-between text-lg font-bold">
                  <span>Total Amount</span>
                  <span>₹{calculateFinalTotal().toLocaleString('en-IN')}</span>
                </div>



                <button
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleCheckout}
                  disabled={itemCount === 0}
                >
                  Proceed to Checkout
                </button>

                <Link
                  to="/products"
                  className="w-full block text-center text-blue-600 hover:underline text-sm mt-2"
                >
                  Continue Shopping
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;